import {
  computed,
  readonly,
  shallowRef,
} from 'vue';
import { useStorage } from '@vueuse/core';
import {
  createOnboardingPayload,
  sanitizeOnboardingDraft,
} from '@/services/onboarding.payload';
import { usePartnerReferralContext } from '@/composables/usePartnerReferralContext';

const STORAGE_KEY = 'qbeek:onboarding-draft';
const availableTariffs = shallowRef([]);

function createInitialDraft() {
  return sanitizeOnboardingDraft();
}

// Раньше черновик лежал в localStorage и переживал закрытие браузера вместе с email,
// телефоном и реквизитами. Теперь он живёт только в sessionStorage вкладки, а долгосрочное
// хранение — на backend (`PATCH /applications/current`) под onboarding-сессией.
if (typeof localStorage !== 'undefined') {
  localStorage.removeItem(STORAGE_KEY);
}

const draft = useStorage(STORAGE_KEY, createInitialDraft(), sessionStorage, {
  mergeDefaults: true,
  serializer: {
    read: (raw) => sanitizeOnboardingDraft(JSON.parse(raw)),
    write: (value) => JSON.stringify(sanitizeOnboardingDraft(value)),
  },
});

export function useOnboardingDraft() {
  const { referralContext } = usePartnerReferralContext();
  const hasAccount = computed(() => Boolean(draft.value.account.email));
  const onboardingPayload = computed(() => createOnboardingPayload(
    draft.value,
    referralContext.value,
  ));

  function getOnboardingPayload() {
    return createOnboardingPayload(draft.value, referralContext.value);
  }

  function updateAccount(payload) {
    draft.value.account = sanitizeOnboardingDraft({
      account: { ...draft.value.account, ...payload },
    }).account;
  }

  function selectCompany(company) {
    draft.value.company = company ? { ...company } : null;
  }

  function updateCompanyQuery(value) {
    draft.value.companyQuery = value;
    if (draft.value.company && value !== draft.value.company.inn && value !== draft.value.company.name) {
      draft.value.company = null;
    }
  }

  function updateRepresentative(payload) {
    draft.value.representative = {
      ...draft.value.representative,
      ...payload,
    };
  }

  function updateBank(payload) {
    draft.value.bank = {
      ...draft.value.bank,
      ...payload,
    };
  }

  function replacePoints(points) {
    draft.value.points = points.map((point) => ({ ...point }));
  }

  function addPoint() {
    draft.value.points.push({
      id: `point-${Date.now()}-${draft.value.points.length + 1}`,
      address: '',
      name: '',
      posSystem: '',
      phone: '',
      contactName: '',
      isOpen: true,
    });
  }

  function updatePoint(pointId, payload) {
    const point = draft.value.points.find((item) => item.id === pointId);
    if (!point) return;
    Object.assign(point, payload);
  }

  function removePoint(pointId) {
    draft.value.points = draft.value.points.filter((point) => point.id !== pointId);
  }

  function togglePoint(pointId) {
    const point = draft.value.points.find((item) => item.id === pointId);
    if (!point) return;
    point.isOpen = !point.isOpen;
  }

  function updateTariff(payload) {
    draft.value.tariff = {
      ...draft.value.tariff,
      ...payload,
    };
  }

  function updateDocuments(payload) {
    draft.value.documents = {
      ...draft.value.documents,
      ...payload,
    };
  }

  function markSubmitted(status = true, requestId = '') {
    draft.value.submitted = status;
    draft.value.requestId = requestId;
  }

  function setAvailableTariffs(tariffs) {
    availableTariffs.value = Array.isArray(tariffs)
      ? [...tariffs].sort((left, right) => left.weight - right.weight || left.code.localeCompare(right.code))
      : [];
    if (!availableTariffs.value.length) return;

    const selected = availableTariffs.value.find(
      (tariff) => tariff.tariffId === draft.value.tariff.tariffId,
    ) || availableTariffs.value.find(
      (tariff) => tariff.id === draft.value.tariff.planId,
    ) || availableTariffs.value[0];

    updateTariff({
      planId: selected.id,
      tariffId: selected.tariffId,
    });
  }

  function restoreDraft(savedDraft = {}) {
    draft.value = sanitizeOnboardingDraft(savedDraft);
  }

  function resetDraft() {
    draft.value = createInitialDraft();
  }

  return {
    draft: readonly(draft),
    availableTariffs: readonly(availableTariffs),
    hasAccount,
    onboardingPayload,
    getOnboardingPayload,
    updateAccount,
    updateCompanyQuery,
    selectCompany,
    updateRepresentative,
    updateBank,
    replacePoints,
    addPoint,
    updatePoint,
    removePoint,
    togglePoint,
    updateTariff,
    setAvailableTariffs,
    updateDocuments,
    markSubmitted,
    restoreDraft,
    resetDraft,
  };
}
